import { useEffect, useState } from "react";
import { api, type PcfDetalhe } from "../lib/api";
import { dateBR, dateTimeBR, formatMinutes, formatDuration, formatDecimal } from "../lib/format";
import { MsgModal } from "./MsgModal";

interface Props {
  numpcf: number;
  onClose: () => void;
}

export function PcfTimelineModal({ numpcf, onClose }: Props) {
  const [data, setData] = useState<PcfDetalhe | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    setData(null);
    setErro(null);
    api.pcf(numpcf).then(setData).catch((e) => setErro((e as Error).message));
  }, [numpcf]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
        <header className="modal-header">
          <div>
            <div className="modal-eyebrow">PCF</div>
            <h2>
              {numpcf}
              {data && <small> — {data.cabecalho.cliente}</small>}
            </h2>
          </div>
          <button className="btn-secundario" onClick={onClose} title="Fechar (Esc)">Fechar</button>
        </header>

        <MsgModal texto={erro} tipo="erro" onClose={() => setErro(null)} />

        {!data && !erro && <div className="status-loading">Carregando…</div>}

        {data && (
          <>
            <section className="modal-cabecalho">
              <div className="dado">
                <div className="dado-label">Pedido</div>
                <div className="dado-valor">
                  {data.cabecalho.numped ? data.cabecalho.numped : "—"}
                  {data.cabecalho.sequen ? (
                    <small> / seq. {data.cabecalho.sequen}</small>
                  ) : null}
                </div>
              </div>
              <div className="dado">
                <div className="dado-label">Tipo</div>
                <div className="dado-valor">{data.cabecalho.tipo || "—"}</div>
              </div>
              <div className="dado">
                <div className="dado-label">Situação</div>
                <div className="dado-valor">
                  <span className="badge-pcf">{data.cabecalho.situacPcf}</span>
                </div>
              </div>
              <div className="dado">
                <div className="dado-label">Data entrega</div>
                <div className="dado-valor">{dateBR(data.cabecalho.dataEntrega)}</div>
              </div>
              <div className="dado dado-wide">
                <div className="dado-label">Produto</div>
                <div className="dado-valor">{data.cabecalho.produto || "—"}</div>
              </div>
              <div className="dado">
                <div className="dado-label">Data envio</div>
                <div className="dado-valor">{dateBR(data.cabecalho.dataEnvio) || "—"}</div>
              </div>
              <div className="dado">
                <div className="dado-label">Peças</div>
                <div className="dado-valor num">{formatDecimal(data.cabecalho.qtdPec)}</div>
              </div>
              <div className="dado">
                <div className="dado-label">Peso teórico</div>
                <div className="dado-valor num">
                  {formatDecimal(data.cabecalho.pesoTeorico, 3)} <small>kg</small>
                </div>
              </div>
              <div className="dado">
                <div className="dado-label">Metros</div>
                <div className="dado-valor num">
                  {formatDecimal(data.cabecalho.qtdMet, 3)} <small>m</small>
                </div>
              </div>
              <div className="dado">
                <div className="dado-label">Comprimento</div>
                <div className="dado-valor num">
                  {formatDecimal(data.cabecalho.comprimento, 0)} <small>mm</small>
                </div>
              </div>
            </section>

            <section className="modal-progresso">
              <h3>Progresso das etapas</h3>
              <TimelineEtapas etapas={data.etapas} />
            </section>

            <section className="modal-historico">
              <h3>Histórico</h3>
              {data.historico.length === 0 ? (
                <div className="empty">Nenhum evento registrado ainda.</div>
              ) : (
                <ul className="historico-list">
                  {data.historico.map((h, i) => (
                    <li key={i} className={`ev ev-${h.situac.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                      <div className="ev-dot" />
                      <div className="ev-content">
                        <div className="ev-linha1">
                          <strong>{h.descricaoEtapa}</strong>
                          <span className={`ev-badge sit-${h.situac.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                            {h.situac}
                          </span>
                          {h.operador && <span className="ev-op">{h.operador}</span>}
                        </div>
                        <div className="ev-linha2">
                          {h.inicio && <span>Início: {dateTimeBR(h.inicio)}</span>}
                          {h.fim && <span>Fim: {dateTimeBR(h.fim)}</span>}
                          {h.duracaoSeg !== null && (
                            <span>Duração: {formatDuration(h.duracaoSeg)}</span>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}

export function TimelineEtapas({ etapas }: { etapas: PcfDetalhe["etapas"] }) {
  return (
    <div className="timeline">
      {etapas.map((et, i) => {
        const state = mapState(et.situacEtapa, et.eAtual);
        const excedeu =
          et.tempoLimiteMin > 0 && et.tempoProduzidoMin > et.tempoLimiteMin;
        return (
          <div key={et.posicao} className={`tl-step tl-${state}`}>
            <div className="tl-line-wrap">
              {i > 0 && <div className={`tl-line before ${state}`} />}
              <div className="tl-marker">{markerIcon(state)}</div>
              {i < etapas.length - 1 && <div className={`tl-line after ${state}`} />}
            </div>
            <div className="tl-label">
              <div className="tl-nome">{et.descricaoEtapa}</div>
              <div className="tl-tempo">
                {state === "done" && `${formatMinutes(et.tempoProduzidoMin)}`}
                {state === "active" && (
                  <span className={excedeu ? "excedeu" : ""}>
                    {formatMinutes(et.tempoProduzidoMin)}
                    <small> / {formatMinutes(et.tempoLimiteMin)}</small>
                  </span>
                )}
                {state === "pending" && (
                  <span className="pending">Limite: {formatMinutes(et.tempoLimiteMin)}</span>
                )}
                {state === "cancel" && <span className="cancel">Cancelada</span>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function mapState(situac: string, eAtual: boolean): "done" | "active" | "pending" | "cancel" {
  if (situac === "FINALIZADA") return "done";
  if (situac === "CANCELADA") return "cancel";
  if (eAtual || situac === "EM PRODUÇÃO") return "active";
  return "pending";
}

function markerIcon(state: string): string {
  switch (state) {
    case "done":
      return "✓";
    case "active":
      return "●";
    case "cancel":
      return "×";
    default:
      return "";
  }
}
